import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { getAiRuntime } from '../runtime';
import { FileItem, ModuleSuggestionsVO, _chatSuggestions } from '../services/aiAssistant';
import { clearPendingChat, readPendingChat } from '../utils/aiAssistantOidc';
import AIDrawer from './AIDrawer';
import { useAIContextStore } from './contextStore';
import FloatingButton from './FloatingButton';
import { getLocalSuggestions } from './localSuggestions';
import { resolveModule, shouldHideFloating } from './moduleMap';

// 权限中心（/micro/getAuthCenterData）下发的 AI 助手权限项标识
const AI_AUTH_KEY = '/ai-assistant(.*)';

interface AutoSendPayload {
  message: string;
  files?: FileItem[];
  projectId?: string;
}

export default function FloatingAI() {
  const runtime = getAiRuntime();

  // FloatingAI 挂在 rootContainer（Router 外，无 plugin-model context），
  // 不能用 useModel('user')；直接走 react-query 拉权限项，token 存在时才发起。
  const { data: authInfo } = useQuery<string[]>({
    queryKey: ['/micro/getAuthCenterData', 'prome-core-ai'],
    queryFn: () => (runtime.getAuthKeys?.() ?? Promise.resolve([])) as Promise<string[]>,
    enabled: !!runtime.getAuthKeys && !!runtime.getToken(),
    staleTime: 5 * 60 * 1000
  });
  // 开放权限
  const hasAiAuth = (authInfo ?? []).includes(AI_AUTH_KEY) || true;
  const ctx = useAIContextStore((s) => s.ctx);
  const setCtx = useAIContextStore((s) => s.setCtx);

  const [pathname, setPathname] = useState<string>(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<ModuleSuggestionsVO | null>(null);
  const [autoSend, setAutoSend] = useState<AutoSendPayload | null>(null);
  const oidcResumedRef = useRef(false);

  /* ─── 监听路由变化（rootContainer 在 Router 外，用 runtime.subscribeRoute 代替 useLocation）─── */
  useEffect(() => {
    setPathname(window.location.pathname);
    return runtime.subscribeRoute(setPathname);
  }, [runtime]);

  const hidden = shouldHideFloating(pathname) || !hasAiAuth;

  /* ─── OIDC 授权回来：检测到 floating pending → 自动打开抽屉并重发 ─── */
  useEffect(() => {
    if (oidcResumedRef.current) return;
    if (hidden) return;
    const pending = readPendingChat();
    if (!pending?.floating || !pending.message) return;
    if (pending.returnPath && pending.returnPath !== pathname) return;
    oidcResumedRef.current = true;
    clearPendingChat();
    setAutoSend({
      message: pending.message,
      files: pending.files,
      projectId: pending.projectId
    });
    setOpen(true);
  }, [pathname, hidden]);

  /* ─── 路由变化兜底：未用 Bread 的页面也能算出 moduleId ─── */
  useEffect(() => {
    const meta = resolveModule(pathname);
    if (!meta) {
      setCtx({ moduleId: undefined, pageTitle: undefined, breadcrumb: undefined, url: window.location.href });
      return;
    }
    setTimeout(() => {
      const cur = useAIContextStore.getState().ctx;
      if (cur.moduleId !== meta.moduleId) {
        setCtx({
          moduleId: meta.moduleId,
          pageTitle: meta.pageTitle,
          breadcrumb: undefined,
          url: window.location.href
        });
      } else {
        setCtx({ url: window.location.href });
      }
    }, 0);
  }, [pathname, setCtx]);

  /* ─── Cmd/Ctrl + K 快捷键 ─── */
  useEffect(() => {
    if (hidden) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hidden]);

  /* ─── 抽屉打开 / moduleId 变化 → 拉气泡 ─── */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await _chatSuggestions({
          moduleId: ctx.moduleId,
          gameId: ctx.gameId ?? runtime.getGameId?.()
        });
        if (!cancelled && res?.suggestions?.length) {
          setSuggestions(res);
          return;
        }
      } catch {
        // 后端没上线 / 失败 → 走兜底
      }
      if (!cancelled) {
        setSuggestions(getLocalSuggestions(ctx.moduleId));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, ctx.moduleId, ctx.gameId, runtime]);

  if (hidden) return null;

  return (
    <>
      <FloatingButton onClick={() => setOpen(true)} hidden={open} />
      <AIDrawer
        open={open}
        onClose={() => setOpen(false)}
        suggestions={suggestions}
        autoSend={autoSend}
        onAutoSendConsumed={() => setAutoSend(null)}
      />
    </>
  );
}
